import { Injectable } from '@angular/core';
import {
  Category, OrderItem, OrderItemChoice, OrderItemChoiceGroup,
  Product,
  ProductChoice, ProductChoiceGroup, ProductIngredient,
  ShoppingCart,
  ShoppingCartItem,
  ShoppingCartOption
} from '../restaurant-manager.models';
import { ApiService } from './api.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import * as moment_ from 'moment';
import { RestaurantService } from './restaurant.service';
const moment = moment_;

@Injectable({
  providedIn: 'root'
})
export class ShoppingCartService {
  public shoppingCart: ShoppingCart = null;

  constructor(
    protected apiService: ApiService,
    protected restaurantService: RestaurantService
  ) {
    const shoppingCart = JSON.parse(localStorage.getItem('shopping_cart'));
    if (shoppingCart?.createdAt && moment().diff(moment(shoppingCart.createdAt), 'hours') < 24) {
      this.shoppingCart = new ShoppingCart(shoppingCart);
      return;
    }
    this.createNewShoppingCart();
  }

  createNewShoppingCart() {
    this.shoppingCart = new ShoppingCart({ createdAt: moment().format() });
    this.save();
  }

  getOptions(): Observable<ShoppingCartOption[]> {
    return this.apiService.get<ShoppingCartOption[]>('/shopping-cart-options').pipe(map(options => {
      return options.map(o => new ShoppingCartOption(o));
    }));
  }

  getOptionsByRestaurant(restaurantId: string): Observable<ShoppingCartOption[]> {
    return this.apiService.get<ShoppingCartOption[]>('/restaurants/' + restaurantId + '/shopping-cart-options').pipe(map(options => {
      return options.map(o => new ShoppingCartOption(o));
    }));
  }

  count(): number {
    return this.shoppingCart ? this.shoppingCart.items.length : 0;
  }

  public save(): void {
    localStorage.setItem('shopping_cart', JSON.stringify(this.shoppingCart));
  }

  set(shoppingCartItems: ShoppingCartItem[]) {
    this.shoppingCart.items = shoppingCartItems;
    this.save();
  }

  add(shoppingCartItem: ShoppingCartItem) {
    shoppingCartItem.id = uuidv4();
    this.shoppingCart.items.push(shoppingCartItem);
    this.save();
  }

  reorderItemsByCategories(categories: Category[]) {
    const filteredCategories = categories.filter(c => this.shoppingCart.items.findIndex(sc => sc.base.category.id === c.id) !== -1);
    const shoppingCartItemsOrderedByCategories: { [key: string]: ShoppingCartItem[] } = {};
    for (const c of filteredCategories) {
      shoppingCartItemsOrderedByCategories[c.id] = this.shoppingCart.items.filter(sc => sc.base.category.id === c.id);
    }
    const shoppingCartItemsReordered = [];
    for (const value of Object.values(shoppingCartItemsOrderedByCategories)) {
      for (const i of value) {
        shoppingCartItemsReordered.push(i);
      }
    }
    if (shoppingCartItemsReordered.length !== this.shoppingCart.items.length) {
      return;
    }
    this.set(shoppingCartItemsReordered);
  }

  update(shoppingCartItem: ShoppingCartItem) {
    const index = this.shoppingCart.items.findIndex(i => i.id === shoppingCartItem.id);
    this.shoppingCart.items[index] = shoppingCartItem;
    const unvalidItemIndex = this.shoppingCart.unvalidItems.findIndex(i => i === shoppingCartItem.id);
    if (unvalidItemIndex !== -1) {
      this.shoppingCart.unvalidItems.splice(unvalidItemIndex, 1);
    }
    this.save();
  }

  remove(shoppingCartItem: ShoppingCartItem) {
    const index = this.shoppingCart.items.findIndex(i => i.id === shoppingCartItem.id);
    this.shoppingCart.items.splice(index, 1);
    this.save();
  }

  getCountByProductId(productId: string): number {
    let count = 0;
    for (const i of this.shoppingCart.items) {
      if (i.base.product.id === productId) {
        count += i.base.quantity;
      }
    }
    return count;
  }

  getItems(): ShoppingCartItem[] {
    return this.shoppingCart.items;
  }

  total(): number {
    let total = 0;
    for (const item of this.shoppingCart.items) {
      total += item.total();
    }
    return total;
  }

  vat(): number {
    let vat = 0;
    for (const item of this.shoppingCart.items) {
      vat += item.vat() * item.base.quantity;
    }
    return vat;
  }

  emptyCart() {
    this.createNewShoppingCart();
    this.save();
  }

  convertToOrderItem(item: ShoppingCartItem, type: 'product' | 'menu_item' = 'product'): OrderItem {
    const choiceGroups: OrderItemChoiceGroup[] = [];
    for (const cg of Object.values(item.choiceGroups)) {
      const choices: OrderItemChoice[] = [];
      for (const c of cg.choices) {
        choices.push(new OrderItemChoice({
          id: uuidv4(),
          name: c.choice.name,
          price: c.choice.price,
          quantity: c.quantity,
          total: c.choice.price * c.quantity
        }));
      }
      if (choices.length > 0) {
        choiceGroups.push(new OrderItemChoiceGroup({
          id: uuidv4(),
          name: cg.base.name,
          choices
        }));
      }
    }
    if (item.ingredientsToAdd?.length > 0) {
      const choices: OrderItemChoice[] = [];
      for (const i of Object.values(item.ingredientsToAdd)) {
        choices.push(new OrderItemChoice({
          id: uuidv4(),
          name: i.name,
          price: i.price,
          quantity: 1,
          total: i.price
        }));
      }
      if (choices.length > 0) {
        choiceGroups.push(new OrderItemChoiceGroup({
          id: uuidv4(),
          name: 'Suppléments',
          choices
        }));
      }
    }
    if (item.ingredientsToRemove?.length > 0) {
      const choices: OrderItemChoice[] = [];
      for (const i of Object.values(item.ingredientsToRemove)) {
        choices.push(new OrderItemChoice({
          id: uuidv4(),
          name: i.name,
          price: i.price,
          quantity: 1,
          total: i.price
        }));
      }
      if (choices.length > 0) {
        choiceGroups.push(new OrderItemChoiceGroup({
          id: uuidv4(),
          name: 'Retirer',
          choices
        }));
      }
    }
    return new OrderItem({
      id: uuidv4(),
      type,
      name: item.base.product.name,
      category: item.base.category.name,
      originCategory: new Category({
        id: item.base.category.id,
        name: item.base.category.name,
        tags: item.base.category.tags,
        productionPriority: item.base.category.productionPriority
      }),
      product: item.base.product.name,
      originProduct: new Product({
        id: item.base.product.id,
        name: item.base.product.name,
        productionPriority: item.base.product.productionPriority,
        tags: item.base.product.tags
      }),
      quantity: item.base.quantity,
      price: item.base.product.price,
      vat: Math.round(item.vat()),
      vatRate: {
        id: item.base.product.vatRate.id,
        name: item.base.product.vatRate.name,
        rate: item.base.product.vatRate.rate
      },
      choiceGroups
    });
  }

  isEnabled(c: ProductChoice | ProductIngredient) {
    return c.stocks.find(s => s.isEnabled && s.restaurant.id === this.restaurantService.selectedRestaurant.value.id) !== undefined;
  }

  enabledChoices(choices: ProductChoice[]) {
    return choices.filter(c => this.isEnabled(c));
  }

  availableChoices(cg: ProductChoiceGroup): ProductChoice[] {
    const choices = [];
    for (const c of cg.choices) {
      if (this.hasStocks(c) && this.isEnabled(c)) {
        choices.push(c);
      }
    }
    return choices;
  }

  hasStocks(c: ProductChoice | ProductIngredient): boolean {
    if (c.stocks.length === 0) {
      return false;
    }
    return c.stocks.findIndex(s => s.hasStock && s.restaurant.id === this.restaurantService.selectedRestaurant.value.id) !== -1;
  }

  createShoppingCartItem(product: Product, category: Category, quantity: number = 1): ShoppingCartItem {
    const choiceGroups = {};
    for (const cg of product.choiceGroups) {
      /*
      let choices = [];
      if (cg.required) {
          const availableChoices = this.availableChoices(cg);
          if (availableChoices?.length > 0) {
              let defaultChoice = availableChoices.find(c => c.isDefault);
              if (!defaultChoice) {
                  defaultChoice = availableChoices[0];
              }
              choices = [{ choice: defaultChoice, quantity: cg.minChoices > 0 ? cg.minChoices : 1 }];
          }
      }
      */
      choiceGroups[cg.id] = { base: cg, choices: [] };
    }
    return new ShoppingCartItem(
      {
        base: {
          product,
          category,
          quantity
        },
        choiceGroups,
        ingredientsToRemove: [],
        ingredientsToAdd: []
      }
    );
  }

}
